// src/features/dashboard/hooks/useEmployeeStats.ts

import {
  useEffect,
  useState,
} from 'react';

import {
  AuthSession,
} from '../../../utils/authSession';

import {
  getEmployeeStats,
} from '../services/employeeStats.service';

import type {
  EmployeeStatsResponse,
} from '../types/employeeStats.types';

import {
  useDashboardDate,
} from '../context/DashboardDateContext';

import {
  formatApiDate,
} from '../utils/date.utils';


/* ============================================================
   EMPLOYEE STATS HOOK
============================================================ */

export const useEmployeeStats = () => {

  /* ==========================================================
     DASHBOARD DATE

     These dates are local calendar dates.

     Example:

       fromDate = 13-Sep-2026
       toDate   = 13-Sep-2026
  ========================================================== */

  const {
    fromDate,
    toDate,
  } = useDashboardDate();


  /* ==========================================================
     EMPLOYEE CODE
  ========================================================== */

  const employeeCode =
    AuthSession.currentEmployee();


  /* ==========================================================
     DATA STATE
  ========================================================== */

  const [
    data,
    setData,
  ] = useState<EmployeeStatsResponse | null>(
    null,
  );


  /* ==========================================================
     LOADING STATE
  ========================================================== */

  const [
    loading,
    setLoading,
  ] = useState<boolean>(
    false,
  );


  /* ==========================================================
     ERROR STATE
  ========================================================== */

  const [
    error,
    setError,
  ] = useState<string | null>(
    null,
  );


  /* ==========================================================
     FORMAT DATE FOR API

     IMPORTANT:

     DO NOT USE:

       fromDate.toISOString().split('T')[0]

     because toISOString() converts the local date to UTC.

     Example:

       Local:
       13-Sep-2026 00:00 IST

       UTC conversion:
       12-Sep-2026 18:30 UTC

     This causes the API to receive:

       2026-09-12

     instead of:

       2026-09-13

     formatApiDate() uses local:

       getFullYear()
       getMonth()
       getDate()

     so the selected calendar date is preserved.
  ========================================================== */

  const fromDateApi =
    formatApiDate(
      fromDate,
    );

  const toDateApi =
    formatApiDate(
      toDate,
    );


  /* ==========================================================
     DEBUG DATE VALUES

     Example:

       UI From Date:
       13-Sep-2026

       API From Date:
       2026-09-13
  ========================================================== */

  useEffect(() => {

    console.log(
      '==========================================',
    );

    console.log(
      'EMPLOYEE STATS DATE',
    );

    console.log(
      'Selected From Date:',
      fromDate,
    );

    console.log(
      'Selected To Date:',
      toDate,
    );

    console.log(
      'API From Date:',
      fromDateApi,
    );

    console.log(
      'API To Date:',
      toDateApi,
    );

    console.log(
      '==========================================',
    );

  }, [
    fromDate,
    toDate,
    fromDateApi,
    toDateApi,
  ]);


  /* ==========================================================
     FETCH EMPLOYEE STATS
  ========================================================== */

  useEffect(() => {

    let cancelled =
      false;


    /* ========================================================
       FETCH FUNCTION
    ======================================================== */

    const fetchData =
      async () => {

        /* ====================================================
           EMPLOYEE CODE VALIDATION
        ==================================================== */

        if (!employeeCode) {

          setData(null);

          setLoading(false);

          return;
        }


        try {

          /* ==================================================
             START LOADING
          ================================================== */

          setLoading(true);

          setError(null);


          /* ==================================================
             API PAYLOAD

             IMPORTANT:

             Use formatted local dates.

             NEVER use:

               fromDate.toISOString()
               toDate.toISOString()
          ================================================== */

          const payload = {

            employeeCode:
              employeeCode,

            fromDate:
              fromDateApi,

            toDate:
              toDateApi,

          };


          /* ==================================================
             DEBUG API PAYLOAD
          ================================================== */

          console.log(
            '==========================================',
          );

          console.log(
            'EMPLOYEE STATS API REQUEST',
          );

          console.log(
            'Employee Code:',
            employeeCode,
          );

          console.log(
            'From Date:',
            fromDateApi,
          );

          console.log(
            'To Date:',
            toDateApi,
          );

          console.log(
            'Request Payload:',
            payload,
          );

          console.log(
            '==========================================',
          );


          /* ==================================================
             API CALL
          ================================================== */

          const response =
            await getEmployeeStats(
              payload,
            );


          /* ==================================================
             IGNORE OLD REQUEST

             This protects against an older API response
             overwriting the newly selected date's data.
          ================================================== */

          if (cancelled) {

            return;
          }


          /* ==================================================
             SET RESPONSE
          ================================================== */

          setData(
            response,
          );


          /* ==================================================
             SUCCESS LOG
          ================================================== */

          console.log(
            'EMPLOYEE STATS API SUCCESS',
          );

          console.log(
            'Response:',
            response,
          );

        } catch (err) {

          /* ==================================================
             IGNORE CANCELLED REQUEST
          ================================================== */

          if (cancelled) {

            return;
          }


          /* ==================================================
             ERROR LOG
          ================================================== */

          console.error(
            'Employee stats error:',
            err,
          );


          /* ==================================================
             ERROR STATE
          ================================================== */

          setError(
            'Failed to fetch stats',
          );


          /* ==================================================
             CLEAR OLD DATA
          ================================================== */

          setData(
            null,
          );

        } finally {

          /* ==================================================
             STOP LOADING
          ================================================== */

          if (!cancelled) {

            setLoading(
              false,
            );
          }
        }
      };


    /* ========================================================
       EXECUTE API CALL
    ======================================================== */

    fetchData();


    /* ========================================================
       CLEANUP
    ======================================================== */

    return () => {

      cancelled = true;

    };

  }, [
    employeeCode,
    fromDateApi,
    toDateApi,
  ]);


  /* ==========================================================
     RETURN
  ========================================================== */

  return {
    data,
    loading,
    error,
  };
};